import type { JSX } from "react";
import { useEffect } from "react";
import { Markdown } from "../components/chat/Markdown";
import { ReleaseSections } from "./ReleaseSections";
import type { WhatsNewEntry } from "./entries";

/**
 * Full release-note detail for a What's New entry — a premium "release page"
 * (openspec/cyclecare-whats-new.md §6) modeled on Anthropic / OpenAI launch
 * posts: a hero (kicker · date → big title → subtitle → media) over a richly
 * typeset markdown body. All styling is token-based, so it adapts to the user's
 * light/dark theme. The optional hero fields degrade gracefully — an entry with
 * only title/date/video/body renders the plain layout, unchanged.
 *
 * react-markdown does not render raw HTML, so the demo media is a real element
 * via the `video`/`heroImage` fields (never an HTML tag inside `body`).
 */
interface ReleaseNoteModalProps {
  entry: WhatsNewEntry;
  /** Real release date to show (publish date for flag-gated entries); falls back
   *  to the entry's authoring date when not supplied. */
  displayDate?: string;
  onClose: () => void;
}

export function ReleaseNoteModal({ entry, displayDate, onClose }: ReleaseNoteModalProps): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal rn-modal"
        role="dialog"
        aria-modal="true"
        aria-label={entry.title}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="rn-x" type="button" aria-label="关闭" onClick={onClose}>
          ×
        </button>
        <div className="rn-body">
          <header className="rn-hero">
            <div className="rn-kicker">
              {entry.kicker ? <span className="rn-eyebrow">{entry.kicker}</span> : null}
              <span className="rn-date">{displayDate ?? entry.date}</span>
            </div>
            <h1 className="rn-title">{entry.title}</h1>
            {entry.subtitle ? <div className="rn-sub">{entry.subtitle}</div> : null}
            {entry.video ? (
              <video
                className="rn-media"
                src={entry.video}
                controls
                // Disallow saving the clip: drop the download item from the
                // controls overflow menu and block the right-click "save video as".
                // (Not DRM — the asset URL is still reachable; this removes the
                // obvious affordances, which is the intent for in-app promos.)
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                preload="metadata"
                {...(entry.heroImage ? { poster: entry.heroImage } : {})}
              />
            ) : entry.heroImage ? (
              <img className="rn-media" src={entry.heroImage} alt="" />
            ) : null}
          </header>
          <div className="rn-prose">
            {entry.sections && entry.sections.length > 0 ? (
              <ReleaseSections sections={entry.sections} />
            ) : entry.body ? (
              <Markdown>{entry.body}</Markdown>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
